/* */
import React, { useCallback, useEffect, useMemo } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalHeader, ModalBody, ModalFooter, Input, FormFeedback } from 'reactstrap';
import { Formik } from 'formik';

import { isInvalidField } from 'helpers';
import { validationPaymentCreate } from 'consts/validateSchema';
import actions from 'redux/actions';
import Loading from 'components/Loading';
import getTheme from 'redux/selector/getTheme';
import { THEME_TYPE } from 'consts/data';
import tracking from 'helpers/tracking';

const { createCard, resetFormCard } = actions;

const Field = ({
  id,
  type,
  name,
  value,
  handleChange,
  onChange,
  valueLabel,
  placeholder,
  errors = {},
  touched,
  isRequired,
}) => {
  return (
    <div className='payment-row'>
      <label htmlFor={id} className='payment-row__label'>
        {valueLabel}
        {isRequired && <span style={{ color: 'red' }}> *</span>}
      </label>
      <div className='payment-row__input'>
        <Input
          id={id}
          type={type}
          name={name}
          value={value}
          onChange={onChange || handleChange}
          placeholder={placeholder}
          invalid={isInvalidField(touched, errors, name)}
        />
        <FormFeedback>{isInvalidField(touched, errors, name) && errors[name]}</FormFeedback>
      </div>
    </div>
  );
};

export default (props) => {
  const { isOpen, toggle, className } = props;
  const dispatch = useDispatch();
  const paymentReducer = useSelector((state) => state.paymentReducer);
  const stripe = useStripe();
  const elements = useElements();
  const theme = getTheme();

  const { errorAddCard } = paymentReducer;

  useEffect(() => {
    // Reset form card when unmount
    return () => dispatch(resetFormCard());
  }, []);

  const CARD_OPTIONS = useMemo(() => {
    return {
      iconStyle: 'solid',
      style: {
        base: {
          color: theme === THEME_TYPE.dark && 'white',
          border: '1px solid #ccc',
          fontWeight: 400,
          fontFamily: 'inherit',
          fontSize: '14px',
          fontSmoothing: 'antialiased',
        },
      },
    };
  }, [theme]);

  const onSubmit = useCallback(
    (values, propsFormik) => {
      const { setSubmitting } = propsFormik;
      const { email, phone, name, postal_code } = values;
      const info = {
        email,
        phone,
        name,
        postal_code,
      };

      if (!stripe || !elements) {
        // Stripe.js has not loaded yet. Make sure to disable
        // form submission until Stripe.js has loaded.
        return;
      }

      tracking.trackEvent('payment/create-payment-method');
      dispatch(
        createCard({ info, setSubmitting, toggle, stripe, card: elements.getElement(CardElement) })
      );
    },
    [elements]
  );

  return (
    <Formik
      initialValues={{
        email: '',
        phone: '',
        name: '',
        postal_code: '',
      }}
      validationSchema={validationPaymentCreate}
      onSubmit={onSubmit}
    >
      {({ handleSubmit, isSubmitting, values, errors, touched, handleChange }) => (
        <Modal isOpen={isOpen} toggle={toggle} className={className}>
          <form onSubmit={handleSubmit} className='dialog-add-payment'>
            <ModalHeader toggle={toggle}>Create payment method</ModalHeader>
            <ModalBody>
              <div>
                <Field
                  id='name'
                  valueLabel='Name'
                  type='text'
                  name='name'
                  onChange={handleChange}
                  value={values.name}
                  placeholder='Jonh Doe'
                  touched={touched}
                  errors={errors}
                  isRequired
                />
                <Field
                  id='email'
                  type='text'
                  name='email'
                  onChange={handleChange}
                  value={values.email}
                  valueLabel='Email'
                  placeholder='jonhdoe@gmail.com'
                  touched={touched}
                  errors={errors}
                  isRequired
                />
                <Field
                  id='phone'
                  type='text'
                  name='phone'
                  onChange={handleChange}
                  valueLabel='Phone'
                  value={values.phone}
                  placeholder='+12345678999'
                  touched={touched}
                  errors={errors}
                  isRequired
                />
                <Field
                  type='text'
                  id='postal_code'
                  name='postal_code'
                  onChange={handleChange}
                  valueLabel='Postal code'
                  value={values.postal_code}
                  placeholder='94103'
                  touched={touched}
                  errors={errors}
                  isRequired
                />
              </div>
              <div className='payment-card'>
                <CardElement options={CARD_OPTIONS} />
                <FormFeedback style={{ display: 'block' }}>
                  {errorAddCard && errorAddCard.message}
                </FormFeedback>
              </div>
            </ModalBody>
            <ModalFooter>
              <button
                type='submit'
                className={`btn ${isSubmitting ? 'btn-outline-primary' : 'btn-primary'}`}
              >
                {isSubmitting ? <Loading color='primary' /> : 'Create'}
              </button>
              <button type='button' className='btn' onClick={toggle}>
                Close
              </button>
            </ModalFooter>
          </form>
        </Modal>
      )}
    </Formik>
  );
};
